import AdminPageIntro from "../../components/common/AdminPageIntro";
import { getAdminPageIntroConfig } from "../../components/common/adminPageIntroConfig";
import SiteStorage from "../../components/common/SiteStorage";
import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, FormControl, FormLabel } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolderOpen, faFolderPlus, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { faFolder } from "@fortawesome/free-regular-svg-icons";
import { Core } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { Excluded } from "@form-validation/plugin-excluded";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import { MediaService } from "../../../services/MediaService";
import { IMediaItem, ISiteFolder } from "../../../models/Interfaces";
import Loading from "../../components/common/Loading";
import TreeSelect from "../../components/common/TreeSelect";
import MediaUploadPreview, { IMediaUploadPreviewRef } from "./MediaUploadPreview";
import GenericTreeView, { INodeTypeDefinition, ITreeContextMenuItem } from "../../components/common/GenericTreeView";
import { useConfirm } from "../../../config/ConfirmProvider";
import { TreeItemSort } from "../../../config/Enums";
import AppEmptyState from "../../components/common/AppEmptyState";
import AppModal from "../../components/common/AppModal";
import { acceptFolderFiles } from "./folderMedia";
import styles from "./mediaManager.module.scss";

const MediaManager = (): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(true);
  const [siteFolders, setSiteFolders] = useState<Array<ISiteFolder>>([]);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [folderFiles, setFolderFiles] = useState<Array<IMediaItem>>([]);
  const [storageRefresh, setStorageRefresh] = useState(0);
  const storageRevision = useMemo(() => ({ files: folderFiles, refresh: storageRefresh }), [folderFiles, storageRefresh]);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const [currentFolder, setCurrentFolder] = useState<number | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<ISiteFolder | null>(null);

  const serviceRef = useRef<MediaService | null>(null);
  const folderRequestRef = useRef(0);
  const uploadPreviewRef = useRef<IMediaUploadPreviewRef>(null);
  const newFolderFormRef = useRef<HTMLFormElement>(null);
  const newFolderFvRef = useRef<Core | null>(null);

  const confirmation = useConfirm();

  const getAllFolders = async (): Promise<void> => {
    if (serviceRef.current) {
      const folders = await serviceRef.current.getSiteFolders();
      setSiteFolders(folders);
    }
  };

  const getFilesInFolder = async (folderid: number): Promise<void> => {
    if (!serviceRef.current) {
      return;
    }

    const requestId = ++folderRequestRef.current;
    setFolderFiles([]);
    setFilesLoading(true);

    try {
      const files = await serviceRef.current.getFilesForFolder(folderid);
      const accepted = acceptFolderFiles(requestId, folderRequestRef.current, folderid, files);
      if (accepted === null) {
        return;
      }

      setFolderFiles(accepted);
    } catch (error) {
      if (requestId !== folderRequestRef.current) {
        return;
      }

      console.log(error);
      setFolderFiles([]);
    } finally {
      if (requestId === folderRequestRef.current) {
        setFilesLoading(false);
      }
    }
  };

  const handleAddFolder = (): void => {
    if (serviceRef.current) {
      setSelectedFolder(serviceRef.current.newSiteFolder());
    }
  };

  const handleLibraryChange = useCallback((files: Array<IMediaItem>): void => {
    setFolderFiles(files);
  }, []);

  const handleFolderSelect = (selected: ISiteFolder): void => {
    setCurrentFolder(selected.Id);
    setSelectedUrl(selected.Id.toString());
    getFilesInFolder(selected.Id);
  };

  const handleFolderNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (selectedFolder !== null) {
      const { name, value } = event.target;
      setSelectedFolder((prev) => ({
        ...(prev as ISiteFolder),
        [name]: value
      }));
    }
  };

  const getFolderName = (folderid: number): string => {
    if (folderid) {
      const existing = siteFolders.find((folder) => folder.Id === folderid);
      if (existing) {
        return existing.FolderName;
      }
    }

    return "No parent folder selected, new folder will be added to root";
  };

  const handleSetParentFolder = (folder: ISiteFolder): void => {
    if (selectedFolder !== null) {
      setSelectedFolder((prev) => ({
        ...(prev as ISiteFolder),
        ParentId: folder.Id
      }));
    }
  };

  const handleItemDrop = async (mediaitem: IMediaItem, parent: ISiteFolder): Promise<void> => {
    if (serviceRef.current) {
      const res = await serviceRef.current.moveMediatItem(mediaitem.Id, parent.Id);

      if (res) {
        setFolderFiles((prev) => prev.filter((item) => item.Id !== res.Id));
      }
    }
  };

  const treeNodeDefinition = (): Array<INodeTypeDefinition> => [
    {
      parentPropertyName: "ParentId",
      displayPropertyName: "FolderName",
      typeIconDefinition: faFolder,
      onItemSelect: handleFolderSelect,
      allowDrop: true,
      onItemDrop: handleItemDrop
    }
  ];

  const createNewFolderValidator = (): void => {
    if (newFolderFormRef.current && !newFolderFvRef.current) {
      newFolderFvRef.current = createFormValidation(newFolderFormRef.current, {
        fields: {
          FolderName: {
            validators: {
              notEmpty: { message: "Folder name is required" }
            }
          }
        },
        plugins: {
          trigger: new Trigger(),
          bootstrap5: new Bootstrap5(),
          excluded: new Excluded(),
        }
      });
    } else {
      newFolderFvRef.current = null;
    }
  };

  const saveNewFolder = async (): Promise<void> => {
    if (newFolderFvRef.current && serviceRef.current && selectedFolder) {
      const res = await newFolderFvRef.current.validate();

      if (res === "Valid") {
        const saved = await serviceRef.current.saveFolder(selectedFolder);

        if (saved) {
          setSiteFolders((prev) => {
            const existingIndex = prev.findIndex((folder) => folder.Id === saved.Id);
            if (existingIndex === -1) {
              return [...prev, saved];
            }

            return prev.map((folder) => folder.Id === saved.Id ? saved : folder);
          });
          setSelectedFolder(null);
        }
      }
    }
  };

  const handleCreateFolder = (selected: ISiteFolder): void => {
    if (serviceRef.current) {
      const newfolder = serviceRef.current.newSiteFolder(selected.Id ?? null);
      if (newfolder) {
        newfolder.ParentId = selected.Id ?? null;
        setSelectedFolder(newfolder);
      }
    }
  };

  const closeDeletedFolder = useCloseDeletedSelection(selectedFolder, (folder) => folder.Id, () => setSelectedFolder(null));

  const handleDeleteFolder = async (selected: ISiteFolder): Promise<void> => {
    if (serviceRef.current) {
      const confirmed = await confirmation.requestConfirmation(`Delete folder <b>${selected.FolderName}</b>, all subfolders and their files? This action cannot be undone.`, `Delete ${selected.FolderName}`);

      if (confirmed) {
        const removed = await serviceRef.current.deleteFolder(selected.Id);
        setStorageRefresh(value => value + 1);

        if (removed) {
          const removedIds = new Set([selected.Id]);
          let changed = true;
          while (changed) {
            changed = false;
            siteFolders.forEach(folder => {
              if (folder.ParentId != null && removedIds.has(folder.ParentId) && !removedIds.has(folder.Id)) { removedIds.add(folder.Id); changed = true; }
            });
          }
          if (currentFolder != null && removedIds.has(currentFolder)) {
            folderRequestRef.current += 1;
            setFilesLoading(false);
            setCurrentFolder(null);
            setSelectedUrl(null);
            setFolderFiles([]);
          }

          removedIds.forEach(closeDeletedFolder);
          setSiteFolders((prev) => prev.filter((folder) => !removedIds.has(folder.Id)));
        }
      }
    }
  };

  const handleMoveFolder = async (movedfolder: ISiteFolder, targetfolder: ISiteFolder | null): Promise<void> => {
    if (serviceRef.current) {
      movedfolder.ParentId = targetfolder?.Id ?? null;
      const res = await serviceRef.current.saveFolder(movedfolder);

      if (res) {
        setSiteFolders((prev) => prev.map((folder) => folder.Id === res.Id ? res : folder));
      }
    }
  };

  const createTreeCtxMenus = (): Array<ITreeContextMenuItem> => ([
    { ItemText: "Create Folder", callbackFunction: (node: any) => handleCreateFolder(node), ItemIcon: <FontAwesomeIcon icon={faPlus} /> },
    { ItemText: "Delete Folder", callbackFunction: (node: any) => handleDeleteFolder(node), ItemIcon: <FontAwesomeIcon icon={faTimes} /> }
  ]);

  useEffect(() => {
    if (selectedFolder && !newFolderFvRef.current) {
      createNewFolderValidator();
    }
  }, [selectedFolder]);

  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new MediaService();
    }

    const getData = async () => {
      try {
        await getAllFolders();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, []);

  const activeFolder = useMemo(() => {
    if (!currentFolder) {
      return null;
    }

    return siteFolders.find((folder) => folder.Id === currentFolder) ?? null;
  }, [currentFolder, siteFolders]);

  const originalFileCount = useMemo(() => folderFiles.filter((file) => !file.SourceFileId).length, [folderFiles]);

  const folderCountLabel = siteFolders.length === 1 ? "1 folder" : `${siteFolders.length} folders`;
  const fileCountLabel = originalFileCount === 1 ? "1 original file" : `${originalFileCount} original files`;

  return (
    <>
      <div className={styles.hero}>
        <AdminPageIntro {...getAdminPageIntroConfig("/content/mediamanager")!} />
        <div className={styles.storageSummary}><SiteStorage compact revision={storageRevision} /></div>
      </div>
      {loading && <Loading Message="loading site media..." SpinSize="5x" />}

      {!loading && (
        <div className={styles.page}>
          <div className={styles.workspace}>
            <section className={styles.treePanel}>
              <div className={styles.panelHeader}>
                <div className={styles.panelTitleWrap}>
                  <h2 className={styles.panelTitle}>Media folders</h2>
                  <p className={styles.panelSubtle}>
                    Organise reusable uploads into folders so editors can find and reuse media more easily.
                  </p>
                  <div className={styles.panelMeta}>{folderCountLabel}</div>
                </div>

                <Button variant="outline-primary" title="Add folder" onClick={handleAddFolder} className={styles.panelAction}>
                  <FontAwesomeIcon icon={faFolderPlus} />
                  New folder
                </Button>
              </div>
              <div className={styles.panelDivider} aria-hidden="true"></div>

              <div className={styles.treeBody}>
                {siteFolders.length > 0 ? (
                  <div className={styles.treeShell}>
                    <GenericTreeView
                      idPropertyName="Id"
                      nodeTypeDefinitions={treeNodeDefinition()}
                      value={selectedUrl ?? ""}
                      items={siteFolders}
                      style={{ flexGrow: 1 }}
                      rootNodeKeyValue={null}
                      closeOnSelect={false}
                      contextMenuItems={createTreeCtxMenus()}
                      onNodeMove={handleMoveFolder}
                      sortBy={TreeItemSort.Ascending}
                    />
                  </div>
                ) : (
                  <AppEmptyState
                    className={styles.emptyNotice}
                    title="No media folders yet"
                    message="Create your first folder to start uploading and organising reusable site media."
                    icon={faFolderOpen}
                    compact
                  />
                )}
              </div>
            </section>

            <section className={styles.detailPanel}>
              <div className={styles.detailHeader}>
                <div>
                  <h2 className={styles.detailTitle}>{activeFolder ? activeFolder.FolderName : "Select a folder"}</h2>
                  <p className={styles.detailSummary}>
                    {activeFolder
                      ? `Upload, review, and manage files in ${activeFolder.FolderName}. Drag files in, preview them, or reuse them elsewhere in the CMS.`
                      : "Choose a folder from the left to upload, review, and manage reusable site media files."}
                  </p>
                  {activeFolder && (
                    <div className={styles.selectedInfo}>
                      <FontAwesomeIcon icon={faFolder} />
                      {filesLoading ? "Loading files..." : fileCountLabel}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.detailBody}>
                {currentFolder ? (
                  <MediaUploadPreview
                    key={currentFolder}
                    ref={uploadPreviewRef}
                    mediafiles={folderFiles}
                    folderId={currentFolder}
                    folderName={activeFolder?.FolderName ?? ""}
                    loading={filesLoading}
                    onLibraryChange={handleLibraryChange}
                  />
                ) : (
                  <div className={styles.emptyPanel}>
                    <AppEmptyState
                      title="No folder selected"
                      message="Select a media folder to review uploaded files, drag in new ones, and manage reusable assets for the site."
                      icon={faFolderOpen}
                    />
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      )}

      {selectedFolder !== null && (
        <AppModal
          showButtons
          size="md"
          title={selectedFolder.Id > 0 ? "Edit folder" : "Add a folder"}
          onClose={() => setSelectedFolder(null)}
          onDataAction={saveNewFolder}
          dataActionButtonText="Save folder"
          actionButtonVariant="success"
          actionButtonIcon={faSave}
        >
          <Form id="NewFolderForm" ref={newFolderFormRef}>
            <div className="fv-row mb-3">
              <FormLabel htmlFor="ParentFolder">Parent folder</FormLabel>
              <TreeSelect
                items={siteFolders}
                valueKey="Id"
                typeKey=""
                parentKey="ParentId"
                displayKey="FolderName"
                idKey="Id"
                onItemSelect={handleSetParentFolder}
                closeOnSelect={true}
                closeText="Close"
                openText={selectedFolder.ParentId ? getFolderName(selectedFolder.ParentId) : "Select parent folder"}
                rootNodeKeyValue={null}
                selectnodetype="folder"
                initialValue={null}
                typeIcons={[{ type: "default", icon: faFolder, class: "text-primary" }]}
                popoverTitle="Select Folder"
                sortBy={TreeItemSort.Ascending}
              />
              <FormLabel className="mt-1 text-secondary">{getFolderName(selectedFolder.ParentId ?? -99)}</FormLabel>
              <div className="fv-validation-message"></div>
            </div>

            <div className="fv-row mb-3">
              <FormLabel htmlFor="FolderName">Folder name</FormLabel>
              <FormControl
                type="text"
                id="FolderName"
                name="FolderName"
                value={selectedFolder.FolderName}
                onChange={handleFolderNameChange}
                placeholder="New folder name..."
              />
              <div className="fv-validation-message"></div>
            </div>
          </Form>
        </AppModal>
      )}
    </>
  );
};

export default MediaManager;
